/* eslint-disable max-len */
import * as R from 'ramda';
import React, { PureComponent } from 'react';
import { withRouter } from 'react-router';
import { SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import commonUtil from 'utilsModule/common';
import { Loading } from 'utilsModule/components';
import { switchProp, switchPath } from 'utilsModule';
import { getSuperAdminMenu, getClusterAdminMenu, getGroupAdminMenu, getHospitalAdminMenu, getProviderMenu } from 'utilsModule/globalSidebar';
import { actionCreators as messageActionCreators } from 'appModule/message/ducks/message';
import { ROLES, ORG_TYPES } from 'dataModule/constants';
import { withResource, resourceTypes, gettersOf } from 'dataModule/store/resources';
import { SelectProfile } from '../components';

@withResource([
  { resourceType: resourceTypes.CONFIGS, method: 'retrieveRoles', options: { runOnDidMount: true } },
  { resourceType: resourceTypes.USERS },
])
@connect(null, { notify: messageActionCreators.show })
@withNamespaces('auth', { wait: true })
@withRouter
class SelectProfileContainer extends PureComponent {
  componentDidMount() {
    this.mounted = true;

    const { location: { state: { profiles = [] } = {} } } = this.props;
    (R.length(profiles) === 1) && this.selectProfile(R.path([0, 'id'], profiles));
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  selectProfile = (profileId) => {
    const {
      i18n,
      location: { state: { profiles = [] } = {} },
      actionCreators, notify, history,
    } = this.props;

    /**
     * Updating behaviour : 
     * Disabling the entryLocation when selecting profile.
     * The Routing will be set default when successfully.
     */
    // const entryLocation = this.props.state.entryLocation;
    const entryLocation = undefined;
    /**
     * End
     */

    const profile = {
      role: switchProp('role', 'id', profileId, profiles),
      orgId: switchPath(['organization', 'id'], ['id'], profileId, profiles),
    };

    return new Promise((resolve, reject) => {
      actionCreators[resourceTypes.USERS].ajax({
        cargo: { method: 'selectProfile', input: { content: profile } },
        onSuccess: ({ data: selectProfileData }) => {
          if (this.mounted) {
            resolve(selectProfileData);

            const {
              sessionId,
              organization: { type: orgType } = {},
              profile: { type: role } = {},
              demographics: { languagePreference },
            } = R.path(['selectProfile'], selectProfileData);

            sessionStorage.setItem('session-token', sessionId);
            sessionStorage.setItem('user-role', role);
            sessionStorage.setItem('org-type', orgType);

            /* Language Preference Logic */
            /* - If there is language stored in Session Storage, then call API to update user preference */
            /* - This means the user has manually change the language before logging in */
            const sessionStorageLanguage = sessionStorage.getItem('language');
            if (sessionStorageLanguage && sessionStorageLanguage !== languagePreference) {
              actionCreators[resourceTypes.USERS].ajax({
                cargo: {
                  method: 'updateLanguagePreference',
                  input: { content: { languagePreference: sessionStorageLanguage } },
                },
              });
            } else {
              /* - If there is no language stored in Session Storage, use the browser's language to update user preference */
              const browserLanguage = i18n.language;;
              if (browserLanguage && browserLanguage !== languagePreference) {
                actionCreators[resourceTypes.USERS].ajax({
                  cargo: {
                    method: 'updateLanguagePreference',
                    input: { content: { languagePreference: browserLanguage } },
                  },
                });
              }
            }

            /*
              PATCH: Need to reset the status of retrieveCurrent
              so that PrivateRoute-WithLoading can prevent flowing down w/o fetching for currentUser.
              Scenario: Access a private route w/o session-token --> diverted to login, w/o reset --> diverted back to login again
            */
            actionCreators[resourceTypes.USERS].reset({ cargo: { method: 'retrieveCurrent' } });
            actionCreators[resourceTypes.USERS].ajax({
              cargo: { method: 'retrieveCurrent' },
              onSuccess: async ({ data: res }) => {
                const { retrieveCurrent: { uiPermissions = [] } } = res;
                const getSidebarAccess = uiPermissions.filter(i => i.category === 'SIDEBAR' && i.access === 'ALLOW');
                let renderMenu = [];
                switch (true) {
                  case role === ROLES.SUPERADMIN: renderMenu = getSuperAdminMenu(); break;
                  case role === ROLES.ADMIN && orgType === ORG_TYPES.GROUP: renderMenu = getGroupAdminMenu(); break;
                  case role === ROLES.ADMIN && orgType === ORG_TYPES.CLUSTER: renderMenu = getClusterAdminMenu(); break;
                  case role === ROLES.ADMIN && orgType === ORG_TYPES.HOSPITAL_CLINIC: renderMenu = getHospitalAdminMenu(); break;
                  case [ROLES.DOCTOR, ROLES.NURSE, ROLES.CARE_MANAGER, ROLES.PHYSIOTHERAPIST, ROLES.DIETICIAN, ROLES.OTHERS].includes(role):
                    renderMenu = getProviderMenu(); break;
                  default: renderMenu = [];
                }

                renderMenu = renderMenu.filter(i => getSidebarAccess.map(a => a.resource).includes(i.resource));
                if (renderMenu.length === 0 && role === ROLES.ADMIN) {
                  renderMenu.push({ path: 'acl' });
                }
                const route = R.pathOr('', [0, 'path'], renderMenu);
                const path = `/app/wsb/${route}`;
                // Temporary remove Organisation module for HOSPITAL/CLINIC ADMIN because creating of DEPARTMENT is disabled
                if (orgType === 'HOSPITAL_CLINIC' && role.includes('ADMIN')) {
                  history.push(entryLocation || path);
                } else if (orgType === 'HOSPITAL_CLINIC' && [
                  ROLES.DOCTOR,
                  ROLES.NURSE,
                  ROLES.CARE_MANAGER,
                  ROLES.PHYSIOTHERAPIST,
                  ROLES.DIETICIAN,
                  ROLES.OTHERS,
                ].includes(role)) {
                  history.push(entryLocation || path);
                } else if (role === ROLES.ADMIN) {
                  history.push(entryLocation || path);
                } else {
                  history.push(entryLocation || `/app/wsb/${role.includes('ADMIN') ? 'orgs' : 'programs'}`);
                }
                commonUtil.renameDocumentTitle(role, orgType);
              },
            });
          }
        },
        onFailure: ({ error }) => {
          if (this.mounted) {
            reject(new SubmissionError({ _error: error.message }));
            notify({ message: `${error.message}`, type: 'error' });
          }
        },
      });
    });
  }

  render() {
    const {
      data, t,
      location: { state: { profiles = [] } = {} },
    } = this.props;

    // process.env.DEBUG && console.log('%crender SelectProfileContainer', 'font-size: 12px; color: #00b3b3', this.props);

    if (data.status.loading || R.length(profiles) <= 1) return <Loading />;

    // Sanitize data
    const roles = gettersOf(resourceTypes.CONFIGS).getRoles()(data);

    this.initialValues = {
      profile: {
        options: R.map(({
          id: profileId,
          role,
          organization: { name: orgName } = {},
        }) => ({
          id: profileId,
          value: profileId,
          label: `${orgName} - ${t(`common:systemRoles.${_.camelCase(role)}`)}`,
        }))(profiles),
      },
    };

    this.runtimeProps = {
      header: {
        banner: {
          title: t('selectProfile.title'),
          subtitle: t('selectProfile.description'),
        },
      },
    };

    return (
      <SelectProfile
        t={t}
        initialValues={this.initialValues}
        runtimeProps={this.runtimeProps}
        selectProfile={this.selectProfile}
        sendSelectProfile={this.sendSelectProfile}
      />
    );
  }
}

export default SelectProfileContainer;
